import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Loader2, Send, Save } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useAuth } from "@/context/AuthContext"
import { useToast } from "@/hooks/useToast"
import { createJob, getUniqueJobSlug, isNetworkFetchError } from "@/lib/db"
import { jobSchema, type JobFormData } from "@/lib/validators"
import { generateSlug, generateExcerpt } from "@/lib/utils"

export default function PostJobPage() {
  const navigate = useNavigate()
  const { user, firebaseUser } = useAuth()
  const { toast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSavingDraft, setIsSavingDraft] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<JobFormData>({
    resolver: zodResolver(jobSchema) as any,
    defaultValues: {
      title: "",
      companyName: "",
      description: "",
      jobType: "full-time",
      location: "",
      isRemote: false,
      applyUrl: "",
      salaryMin: null,
      salaryMax: null,
    },
  })

  const isRemoteValue = watch("isRemote")

  const submitJob = async (data: JobFormData, status: "draft" | "published") => {
    const authorId = user?.uid || firebaseUser?.id
    if (!authorId) {
      toast({ title: "Please sign in to post a job", variant: "destructive" })
      return
    }

    const setLoading = status === "draft" ? setIsSavingDraft : setIsSubmitting
    setLoading(true)

    try {
      const baseSlug = generateSlug(data.title)
      const slug = status === "draft"
        ? `${baseSlug}-draft-${Date.now()}`
        : await getUniqueJobSlug(baseSlug)

      const excerpt = generateExcerpt(data.description, 200)

      await createJob({
        authorId,
        title: data.title,
        slug,
        companyName: data.companyName,
        description: data.description,
        excerpt,
        jobType: data.jobType,
        location: data.location,
        isRemote: data.isRemote,
        applyUrl: data.applyUrl,
        salaryMin: data.salaryMin || null,
        salaryMax: data.salaryMax || null,
        status,
        expiresAt: null,
      })

      toast({ title: status === "draft" ? "Draft saved!" : "Job posted!" })
      navigate(status === "draft" ? "/dashboard" : `/jobs/${slug}`)
    } catch (error) {
      if (!isNetworkFetchError(error)) {
        console.error("Failed to save job:", error)
      }
      toast({
        title: `Failed to ${status === "draft" ? "save draft" : "post job"}`,
        description: error instanceof Error ? error.message : "Please try again",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const onSubmit = (data: JobFormData) => submitJob(data, "published")
  const handleSaveDraft = () => {
    const data = watch()
    // Minimal validation for drafts
    if (!data.title || !data.companyName) {
      toast({ title: "Title and company name are required", variant: "destructive" })
      return
    }
    submitJob(data as JobFormData, "draft")
  }

  return (
    <div className="container mx-auto max-w-3xl px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Post a Job</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Reach React Native developers looking for their next opportunity.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Job Title */}
        <div className="space-y-2">
          <Label htmlFor="title">Job Title *</Label>
          <Input
            id="title"
            {...register("title")}
            placeholder="e.g. Senior React Native Developer"
          />
          {errors.title && <p className="text-sm text-red-500">{errors.title.message}</p>}
        </div>

        {/* Company Name */}
        <div className="space-y-2">
          <Label htmlFor="companyName">Company Name *</Label>
          <Input
            id="companyName"
            {...register("companyName")}
            placeholder="e.g. Acme Inc."
          />
          {errors.companyName && <p className="text-sm text-red-500">{errors.companyName.message}</p>}
        </div>

        {/* Job Type & Location */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Job Type *</Label>
            <Select defaultValue="full-time" onValueChange={value => setValue("jobType", value as JobFormData["jobType"])}>
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="full-time">Full-time</SelectItem>
                <SelectItem value="part-time">Part-time</SelectItem>
                <SelectItem value="contract">Contract</SelectItem>
                <SelectItem value="freelance">Freelance</SelectItem>
              </SelectContent>
            </Select>
            {errors.jobType && <p className="text-sm text-red-500">{errors.jobType.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="location">Location *</Label>
            <Input
              id="location"
              {...register("location")}
              placeholder="e.g. San Francisco, CA"
            />
            {errors.location && <p className="text-sm text-red-500">{errors.location.message}</p>}
          </div>
        </div>

        {/* Remote toggle */}
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="isRemote"
            {...register("isRemote")}
            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
          />
          <Label htmlFor="isRemote" className="cursor-pointer">This is a remote position</Label>
        </div>

        {/* Salary Range */}
        <div className="space-y-2">
          <Label>Salary Range (optional, USD/year)</Label>
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              type="number"
              {...register("salaryMin", { valueAsNumber: true })}
              placeholder="Min (e.g. 80000)"
            />
            <Input
              type="number"
              {...register("salaryMax", { valueAsNumber: true })}
              placeholder="Max (e.g. 120000)"
            />
          </div>
        </div>

        {/* Apply URL */}
        <div className="space-y-2">
          <Label htmlFor="applyUrl">Application URL *</Label>
          <Input
            id="applyUrl"
            {...register("applyUrl")}
            placeholder="https://yourcompany.com/careers/apply"
          />
          {errors.applyUrl && <p className="text-sm text-red-500">{errors.applyUrl.message}</p>}
        </div>

        {/* Description */}
        <div className="space-y-2">
          <Label htmlFor="description">Job Description *</Label>
          <Textarea
            id="description"
            {...register("description")}
            placeholder="Describe the role, responsibilities, requirements, and benefits..."
            rows={12}
          />
          {errors.description && <p className="text-sm text-red-500">{errors.description.message}</p>}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 border-t border-border pt-6">
          <Button type="button" variant="outline" onClick={handleSaveDraft} disabled={isSavingDraft || isSubmitting}>
            {isSavingDraft ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save Draft
          </Button>
          <Button type="submit" disabled={isSubmitting || isSavingDraft}>
            {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            Publish Job
          </Button>
        </div>
      </form>
    </div>
  )
}
